import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { Viewport } from 'reactflow';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import styled from 'styled-components';

import { BadgeCounter } from '@components/badge-counter';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { useLocalStorageState } from '@components/component-helpers';
import { ErrorScreen } from '@components/error-screen';
import { useConfirmModal } from '@components/generic-modals/confirm-modal';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconBuilder } from '@components/icons/builder';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconHistory } from '@components/icons/history';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconPublish } from '@components/icons/publish';
import { IconSettings } from '@components/icons/settings';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTrash } from '@components/icons/trash';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Menu } from '@components/menu';
import { hideModal } from '@components/modal';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { showError, useToaster } from '@components/toaster';
import { useNavigationBlock } from '@hooks/use-navigation-block';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import {
  useJourneyIntegrationEndpoints,
  useJourneyIntegrationWebhook,
} from 'api/integrations/hooks';
import { useJourneyActionForms } from 'api/journey-action-forms/hooks';
import {
  useJourneyActionAvailableMoves,
  useJourneyActions,
  useMoveJourneyAction,
} from 'api/journey-actions/hooks';
import {
  JourneyActionBreakingReferences,
  listBreakingReferencesByMove,
} from 'api/journey-actions/requests';
import { useJourneyFormsList } from 'api/journey-forms/hooks';
import { useJourneyPollingConfigsList } from 'api/journey-polling/hooks';
import { useJourneyWebhooksList } from 'api/journey-webhooks/hooks';
import {
  useEditJourney,
  useJourney,
  useJourneyIssues,
  usePublishedVersions,
  usePublishJourney,
  useVersionPreview as useVersionPreviewQuery,
} from 'api/journeys/hooks';
import {
  useChangeModifiedTravellerJourneys,
  useGetModifiedTravellerJourneys,
} from 'api/traveller-journeys/hooks';
import { BASE_ACTION_TYPE, JourneyAction, JourneyType } from 'api/types';
import { useEditJourneyActionFormOverlay } from 'ui/actions/combo-send-form/edit-journey-action-form-overlay';
import { useTriggersCollapsedStore } from 'ui/actions/triggers/common';
import { useEditJourneyFormOverlay } from 'ui/actions/triggers/journey-form/edit-journey-form-overlay';
import { useAddTravellersSidebar } from 'ui/actions/triggers/sidebars/add-trigger-sidebar';
import { TestIdContext, getShortIdToDescriptiveMap } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';
import { JourneyContext, journeyActionFormContextMapType } from 'ui/journey-builder/base-builder';
import { MoveContext, hiddenIdentifier } from 'ui/journey-builder/edge';
import { CollapseContext, useCollapsedActions } from 'ui/journey-builder/hooks/use-collapsed-nodes';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import { JourneyBuilder } from 'ui/journey-builder/journey-builder';
import { useCancelChangesModal } from 'ui/journey-builder/modals/cancel-changes';
import { GenericActionCard, nodeToActionBasic } from 'ui/journey-builder/node-types/common';
import { JourneyNode } from 'ui/journey-builder/node-types/types';
import { JourneyPresenceIndicator } from 'ui/journey-builder/presence/journey-presence-indicator';
import { PresenceProvider } from 'ui/journey-builder/presence/presence-context';
import { showActionSidebar } from 'ui/journey-builder/show-action-sidebar';

import { usePreserveActionSidebar } from './hooks/use-preserve-action-sidebar';
import { useVersionPreview } from './hooks/use-version-preview';
import { JourneyBreadcrumb } from './journey-breadcrumb';
import { JourneyEditContext, useJourneyEditContext } from './journey-edit-context';
import {
  clearPublishingInBackground,
  clearSearchParam,
  getIsPublishingInTheBackground,
  getPreviewVersionParam,
  setPublishingInBackground,
} from './journey-edit-helpers';
import { useJourneyDetailsSidebar } from './journey-edit-sidebar';
import { JourneyFields } from './journey-fields';
import { convertToNodes, ConvertToNodesProps } from './journey-node-utils';
import { JourneySettings } from './journey-settings';
import { useDeleteJourneyModal } from './modals/delete-journey';
import { usePublishAddTravellerActions } from './modals/publish-on-traveller-modal';
import { PreviewModeHeader } from './preview-mode-header';
import { useIssuesSidebar } from './sidebars/issues';

export const JourneyEdit = () => {
  const { t } = useScopedTranslate(locale);
  const { journeyId = '', workspaceId } = useParams();
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const viewportRef = useRef<Viewport | null>(null);
  const activeActionIdRef = useRef<string | null>(null);

  const journeyQuery = useJourney(journeyId, {
    onError: () => {
      showError(t('journeyNotFound'));
      navigate('/');
    },
  });

  const journey = journeyQuery.data;
  const publishMutation = usePublishJourney(journeyId);
  const isPublishingInTheBackground = getIsPublishingInTheBackground(journeyId);
  useRotatingLoadingMessages({
    isEnabled:
      (publishMutation.isLoading || isPublishingInTheBackground) &&
      (journey ? journey.hasUnpublishedChanges : true),
    firstMessage: t('publishing'),
  });

  const integrationQuery = useJourneyIntegrationEndpoints(journeyId ?? '');
  const integrations = integrationQuery.data ?? [];
  const webhookQuery = useJourneyIntegrationWebhook(journeyId ?? '');
  const webhooks = webhookQuery.data ?? [];

  const showEditSidebar = useJourneyDetailsSidebar();
  const updateMutation = useEditJourney();

  const {
    previewVersion,
    setPreviewVersion,
    isPreviewMode,
    handleExitPreviewMode,
    handleOpenVersionHistory,
    handleRestoreVersion,
  } = useVersionPreview({
    journeyShortId: journeyId,
    hasUnpublishedChanges: journey?.hasUnpublishedChanges ?? false,
    basedOnVersionId: journey?.basedOnVersionId,
  });

  const isLoading = journeyQuery.isLoading || integrationQuery.isLoading || webhookQuery.isLoading;

  const contextValue = useMemo(
    () => ({
      isEditingTitle,
      publishMutation,
      previewVersion,
      setPreviewVersion,
      isPreviewMode,
    }),
    [isEditingTitle, publishMutation, previewVersion, isPreviewMode, setPreviewVersion]
  );

  if (!journey) return null;

  const saveJourneyTitle = async (title: string) => {
    if (!title || title === journey.title) return;
    await updateMutation.mutateAsync({ journeyId, title });
  };

  const tabs = [
    {
      label: t('journey'),
      section: 'journey',
      icon: <IconBuilder />,
      content: (
        <JourneyFlow
          journey={journey}
          integrations={integrations}
          webhooks={webhooks}
          viewportRef={viewportRef}
          activeActionIdRef={activeActionIdRef}
        />
      ),
    },
    {
      label: t('fields'),
      section: 'attributes',
      icon: <IconAttributeJourney />,
      content: (
        <JourneyFields
          journeyShortId={journeyId}
          attributes={journeyQuery.data?.attributes ?? []}
        />
      ),
    },
    {
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
      content: <JourneySettings journeyTimezone={journeyQuery.data?.timezone as string} />,
    },
  ];

  if (isPreviewMode && previewVersion) {
    return (
      <PresenceProvider journeyId={journeyId}>
        <JourneyEditContext.Provider value={contextValue}>
          <PreviewModeLayout>
            <PreviewModeContent>
              <PreviewModeHeader
                version={previewVersion}
                onClose={handleExitPreviewMode}
                onOpenVersionHistory={handleOpenVersionHistory}
                onRestore={handleRestoreVersion}
              />
              <JourneyFlow journey={journey} integrations={integrations} webhooks={webhooks} />
            </PreviewModeContent>
          </PreviewModeLayout>
        </JourneyEditContext.Provider>
      </PresenceProvider>
    );
  }

  return (
    <PresenceProvider journeyId={journeyId}>
      <JourneyEditContext.Provider value={contextValue}>
        <RouterTabsHeader
          tabs={tabs}
          actions={
            <JourneyActions journey={journey} onOpenVersionHistory={handleOpenVersionHistory} />
          }
          breadcrumbs={
            <JourneyBreadcrumb
              journey={journey}
              workspaceId={journey.workspace}
              onRename={saveJourneyTitle}
              onIsEditingChange={setIsEditingTitle}
              showEditSidebar={() => {
                return showEditSidebar({ journey, isEditing: true, workspaceId });
              }}
              isBuilder
            />
          }
          isLoading={isLoading}
          backAction={
            <IconButton
              data-cy="journey-back-btn"
              icon={<IconArrowLeft />}
              variant="ghost-purple"
              onClick={() => {
                return navigate(
                  generatePath(routePaths.journeyDetails, {
                    workspaceId: journey.workspace,
                    journeyId: journey.shortId,
                    '*': null,
                  })
                );
              }}
            />
          }
        />
      </JourneyEditContext.Provider>
    </PresenceProvider>
  );
};

const PreviewModeLayout = styled.div({
  display: 'flex',
  height: '100vh',
  width: '100%',
});

const PreviewModeContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

type JourneyProps = { journey: JourneyType };
type JourneyPublishActionProps = JourneyProps & { isDeleting: boolean };

const JourneyPublishAction = ({ journey, isDeleting }: JourneyPublishActionProps) => {
  const { publishMutation } = useJourneyEditContext();
  const cancelModal = useCancelChangesModal();
  const showIssuesSidebar = useIssuesSidebar();
  const issuesQuery = useJourneyIssues(journey.shortId);
  const { t } = useScopedTranslate(locale);
  const modifiedQuery = useGetModifiedTravellerJourneys(journey.shortId);
  const changeTravellers = useChangeModifiedTravellerJourneys(journey.shortId);
  const navigate = useNavigate();
  const showModal = usePublishAddTravellerActions();
  const isPublishing = publishMutation.isLoading || getIsPublishingInTheBackground(journey.shortId);
  const cannotPublish = isPublishing || !journey.isConfigured;

  const [hidePrompt, setHidePrompt] = useLocalStorageState<boolean>({
    key: `journey-publish-prompt-${journey.shortId}`,
    defaultValue: false,
  });

  const issues = issuesQuery.data ?? [];
  const hasIssues = issues.length > 0;
  const hasModifiedTravellers = modifiedQuery.data && modifiedQuery.data?.length > 0;

  const publishJourney = async () => {
    await publishMutation.mutateAsync(undefined);
    if (hasModifiedTravellers) {
      showModal({
        travellers: modifiedQuery.data ?? [],
        onUpdate: (ids: string[]) => {
          changeTravellers.mutateAsync(ids);
          hideModal();
        },
      });
    }
  };

  const runPublishInTheBackground = (ids?: string[]) => {
    setPublishingInBackground(journey.shortId);
    publishMutation.mutate(ids && ids.length > 0 ? { changeTravellerJourneyIds: ids } : undefined, {
      onSettled: () => {
        clearPublishingInBackground(journey.shortId);
      },
    });
  };

  useEffect(() => {
    if (!journey.hasUnpublishedChanges) {
      clearPublishingInBackground(journey.shortId);
    }
  }, [journey.hasUnpublishedChanges, journey.shortId]);

  useNavigationBlock({
    shouldBlock: journey.hasUnpublishedChanges && !hidePrompt && !isPublishing && !isDeleting,
    blockBeforeUnload: false,
    onSave: () =>
      new Promise<void>((resolve) => {
        const travellers = modifiedQuery.data ?? [];
        if (travellers.length > 0) {
          setTimeout(
            () =>
              showModal({
                travellers,
                onUpdate: (ids: string[]) => {
                  runPublishInTheBackground(ids);
                  resolve();
                },
                cancelButtonText: t('keepAsIs'),
                onCancel: () => {
                  runPublishInTheBackground(undefined);
                  resolve();
                },
              }),
            0
          );
          return;
        }
        runPublishInTheBackground(undefined);
        resolve();
      }),
    onDiscard: () =>
      navigate(
        generatePath(routePaths.journeyDetails, {
          workspaceId: journey.workspace,
          journeyId: journey.shortId,
          '*': null,
        })
      ),
    isSaveDisabled: cannotPublish,
    title: t('publishChanges'),
    titleIcon: <IconFormIndicatorWarning />,
    description: t('publishChangesDescription'),
    saveText: t('publishJourney'),
    discardText: t('skipForNow'),
    discardButtonVariant: 'secondary',
    allowedPaths: ['/journeys/{id}/edit', '/journeys/{id}/edit/.*'],
    children: (
      <Checkbox label={t('dontShowAgain')} defaultValue={hidePrompt} onChange={setHidePrompt} />
    ),
  });

  if (!journey.hasPublishedJourney && !journey.hasUnpublishedChanges) {
    return null;
  }

  return (
    <Stack space={2} orientation="horizontal">
      {journey.hasUnpublishedChanges && (
        <Button
          text={hasIssues ? t('issues', { num_issues: issues.length }) : t('noIssues')}
          variant="secondary"
          iconLeft={
            issuesQuery.isLoading ? (
              <SizedRelativeSpinner size={24} $skipMargin />
            ) : hasIssues ? (
              <IconFormIndicatorWarning />
            ) : (
              <IconFormIndicatorSuccess />
            )
          }
          onClick={() =>
            showIssuesSidebar({
              issues,
              numAttributes: journey.attributes.length,
            })
          }
        />
      )}
      {journey.hasUnpublishedChanges && journey.hasPublishedJourney && (
        <Button
          variant="secondary"
          disabled={publishMutation.isLoading}
          onClick={() => cancelModal({ journeyShortId: journey.shortId })}
          text={t('cancelChanges')}
        />
      )}
      {journey.hasUnpublishedChanges && (
        <Button
          disabled={cannotPublish}
          data-cy="publish"
          variant="primary"
          iconLeft={<IconPublish />}
          onClick={() => publishJourney()}
          tooltip={!journey.isConfigured ? t('journeyNotConfigured') : undefined}
          text={journey.hasPublishedJourney ? t('publishChanges') : t('publish')}
        />
      )}
    </Stack>
  );
};

type JourneyActionsProps = JourneyProps & {
  onOpenVersionHistory?: () => void;
};

const JourneyActions = ({ journey, onOpenVersionHistory }: JourneyActionsProps) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useJourneyDetailsSidebar();
  const navigate = useNavigate();
  const showDeleteJourneyModal = useDeleteJourneyModal();
  const [isDeleting, setIsDeleting] = useState(false);
  const currentYear = new Date().getFullYear();
  const datePublished = new Date(journey.datePublished || '');
  const { data: publishedVersions } = usePublishedVersions(journey.shortId);
  const versionCount = publishedVersions?.length ?? 0;

  if (!journey.shortId) {
    return (
      <Stack space={2}>
        <SizedRelativeSpinner size={24} />
      </Stack>
    );
  }

  return (
    <Stack space={2}>
      <Stack space={2} orientation="horizontal" justify="flex-end">
        <JourneyPublishAction journey={journey} isDeleting={isDeleting} />
        <Stack space={2} orientation="horizontal">
          <Menu
            dataTestid="journey-actions-btn"
            buttonProps={{
              variant: 'secondary',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item
              onSelect={() => showEditSidebar({ journey, isEditing: true })}
              icon={<IconEdit />}
              label={t('editJourneyDetails')}
            />
            {journey.hasPublishedJourney && (
              <Menu.Item
                onSelect={() => onOpenVersionHistory?.()}
                icon={<IconHistory />}
                label={t('versionHistory')}
                iconRight={<BadgeCounter value={versionCount} size="sm" />}
              />
            )}
            <Menu.Item
              onSelect={() =>
                showDeleteJourneyModal({ journey, onDelete: () => setIsDeleting(true) })
              }
              dataCy="delete-journey"
              variant="danger"
              label={t('deleteJourney')}
              icon={<IconTrash />}
            />
          </Menu>
          <IconButton
            icon={<IconCloseRegular />}
            onClick={() => {
              navigate(
                generatePath(routePaths.journeyDetails, {
                  workspaceId: journey.workspace,
                  journeyId: journey.shortId,
                  '*': null,
                })
              );
            }}
          />
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} align="center" justify="flex-end">
        {journey.hasUnpublishedChanges ? (
          <TextStatusIndicator variant="info" text={t('draft')} />
        ) : (
          <Stack orientation="horizontal" space={2} align="center">
            <TextStatusIndicator variant="success" text={t('published')} />
            {journey.datePublished && (
              <Text
                color="textHelper"
                text={abbreviateDate(
                  datePublished,
                  'en',
                  datePublished?.getFullYear() === currentYear ? 'datetime-short' : 'datetime'
                )}
              />
            )}
          </Stack>
        )}
        <JourneyPresenceIndicator />
      </Stack>
    </Stack>
  );
};

const JourneyFlow = ({
  journey,
  integrations,
  webhooks,
  viewportRef,
  activeActionIdRef,
}: JourneyProps &
  Pick<ConvertToNodesProps, 'integrations' | 'webhooks'> & {
    viewportRef?: React.MutableRefObject<Viewport | null>;
    activeActionIdRef?: React.MutableRefObject<string | null>;
  }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedNode, setNodes, getValidNodes, getNode } = useNodes();
  const [isDragging, setIsDragging] = useState(false);
  const [shortIdToDescriptiveMap, setShortIdToDescriptiveMap] = useState({});
  const { t } = useScopedTranslate(locale);
  const { isPreviewMode, previewVersion } = useJourneyEditContext();
  const showConfirmModal = useConfirmModal('LARGE');

  const versionPreviewQuery = useVersionPreviewQuery(
    journey.shortId,
    previewVersion?.shortId ?? null
  );
  const moveMutation = useMoveJourneyAction();
  useRotatingLoadingMessages({
    isEnabled: moveMutation.isLoading,
    firstMessage: t('moving'),
  });

  const { showError } = useToaster();
  const isFirstFetch = useRef(true);
  const collapseNodesFunctions = useCollapsedActions();
  const showAddTravellersSidebar = useAddTravellersSidebar();
  const showEditJourneyFormOverlay = useEditJourneyFormOverlay();
  const showEditJourneyActionFormOverlay = useEditJourneyActionFormOverlay();
  const formsQuery = useJourneyFormsList(journey.shortId);
  const webhooksQuery = useJourneyWebhooksList(journey.shortId);
  const pollingConfigsQuery = useJourneyPollingConfigsList(journey.shortId);

  const triggerForms = formsQuery.data ?? [];
  const triggerWebhooks = webhooksQuery.data ?? [];
  const triggerPollingConfigs = pollingConfigsQuery.data ?? [];
  const triggersCount =
    triggerForms.length + triggerWebhooks.length + triggerPollingConfigs.length + 1;
  const areTriggerQueriesReady =
    formsQuery.isSuccess && webhooksQuery.isSuccess && pollingConfigsQuery.isSuccess;
  const { isCollapsed: isTriggersCollapsed } = useTriggersCollapsedStore();

  const journeyActionFormsQuery = useJourneyActionForms({ journeyId: journey.shortId });
  const journeyActionForms = (journeyActionFormsQuery.data ?? []).reduce((acc, form) => {
    if (form.journeyActionId) {
      acc[form.journeyActionId] = { shortId: form.shortId, name: form.name };
    }
    return acc;
  }, {} as journeyActionFormContextMapType);

  const journeyActionsQuery = useJourneyActions(journey.shortId, {
    onSuccess: (data) => {
      if (getPreviewVersionParam()) {
        return;
      }

      if (data.length > 0) {
        if (areTriggerQueriesReady) {
          updateNodes(data);
        }

        if (isFirstFetch.current) {
          isFirstFetch.current = false;

          if (searchParams.get('openTriggers')) {
            clearSearchParam('openTriggers');
            showAddTravellersSidebar({
              journeyShortId: journey.shortId,
            });
            return;
          }

          const journeyFormId = searchParams.get('openJourneyForm');
          if (journeyFormId) {
            setTimeout(() => {
              showEditJourneyFormOverlay(
                {
                  journeyId: journey.shortId,
                  formId: journeyFormId,
                },
                { onHide: () => clearSearchParam('openJourneyForm') }
              );
            }, 250);
            return;
          }

          const journeyActionFormId = searchParams.get('openJourneyActionForm');
          if (journeyActionFormId) {
            clearSearchParam('openJourneyActionForm');
            setTimeout(() => {
              showEditJourneyActionFormOverlay({
                formId: journeyActionFormId,
                openPreview: true,
              });
            }, 250);
            return;
          }
        }
      }
    },
  });
  const availableMovesQuery = useJourneyActionAvailableMoves(journey.shortId, {
    enabled: journeyActionsQuery.isSuccess,
  });

  const validNodes = getValidNodes();

  usePreserveActionSidebar(activeActionIdRef, getNode, validNodes.length > 0);

  useEffect(() => {
    // If the query parameter ?focusedAction=shortid is present,
    // we remove it from the parameters and open the sidebar
    // for that action.
    const focusedAction = searchParams.get('focusedAction');
    if (!focusedAction) return;

    const node = getNode(focusedAction);
    if (!node) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('focusedAction');
    setSearchParams(newParams);

    setTimeout(() => {
      showActionSidebar(node);
    }, 200);
  }, [validNodes.length, getNode, searchParams, setSearchParams]);

  useEffect(() => {
    if (isPreviewMode) return;
    if (!areTriggerQueriesReady) return;

    if (journeyActionsQuery.data?.length) {
      updateNodes(journeyActionsQuery.data);
    }
    //eslint-disable-next-line
  }, [
    journey.collapsedActions.join('-'),
    triggersCount,
    isTriggersCollapsed,
    isPreviewMode,
    areTriggerQueriesReady,
    integrations,
    webhooks,
  ]);

  useEffect(() => {
    if (isPreviewMode && versionPreviewQuery.data) {
      updateNodes(versionPreviewQuery.data);
    } else if (!isPreviewMode && journeyActionsQuery.data?.length) {
      updateNodes(journeyActionsQuery.data);
    }
    //eslint-disable-next-line
  }, [isPreviewMode, versionPreviewQuery.data]);

  const updateNodes = (actions: Array<JourneyAction>) => {
    const descriptiveMap = getShortIdToDescriptiveMap(actions);
    setShortIdToDescriptiveMap(descriptiveMap);
    setNodes(
      convertToNodes({
        actions,
        integrations,
        webhooks,
        collapsedActions: journey.collapsedActions,
        triggersCount,
        isTriggersCollapsed,
      })
    );
  };

  const availableMoves = useMemo(() => {
    return availableMovesQuery.data ?? {};
  }, [availableMovesQuery.data]);

  const handleDragDrop = async (event: DragEndEvent) => {
    setIsDragging(false);
    if (event.over) {
      const actionId = event.active.data.current?.id ?? '';
      const edgeId = event.over.id as string;
      const [, parentId, secondId] = edgeId.split('-');
      const childId = edgeId.endsWith(hiddenIdentifier) ? undefined : secondId;
      let dependants: JourneyActionBreakingReferences = [];
      try {
        dependants = await listBreakingReferencesByMove({
          actionId,
          parentId,
          childId,
        });
      } catch (e) {
        showError(t('core.errors.generic'));
      }
      const dependantsCount = dependants.length ?? 0;
      const moveData = { actionId, parentId, childId, journeyId: journey.shortId };
      if (dependantsCount > 0) {
        const nodes = getValidNodes();

        const relationshipBreaks = dependants.filter(([, , fields]) =>
          fields.includes('relationship')
        );
        const fieldMappingBreaks = dependants.filter(
          ([, , fields]) => !fields.includes('relationship')
        );

        const getConfirmationNodes = (items: [string, string, string[]][]) =>
          items.map(([parentNodeId, childNodeId]) => {
            const parentNode = nodes.find((node) => node.id === parentNodeId);
            const childNode = nodes.find((node) => node.id === childNodeId);
            return [parentNode, childNode];
          }) as [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][];

        const relationshipNodes = getConfirmationNodes(relationshipBreaks);
        const fieldMappingNodes = getConfirmationNodes(fieldMappingBreaks);

        const renderBreakingNodes = (
          confirmationNodes: [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][],
          label: string
        ) => (
          <Stack space={2}>
            <Text font="semibold14">{label}</Text>
            {confirmationNodes.map(([parentNode, childNode]) => (
              <Stack
                key={`${parentNode.id}-${childNode.id}`}
                orientation="horizontal"
                justify="space-between"
                align="center"
                space={1}
              >
                <GenericActionCard action={nodeToActionBasic(childNode)} />
                <IconTextFormatLinkOff color="elementRed100" />
                <GenericActionCard action={nodeToActionBasic(parentNode)} />
              </Stack>
            ))}
          </Stack>
        );

        const hasRelationships = relationshipBreaks.length > 0;
        const hasFieldMappings = fieldMappingBreaks.length > 0;
        let description = t('confirmDescription');
        if (hasRelationships && !hasFieldMappings) {
          description = t('confirmDescriptionRelationship');
        } else if (hasRelationships && hasFieldMappings) {
          description = t('confirmDescriptionBoth');
        }

        showConfirmModal({
          title: t('confirmTitle'),
          headerIcon: <IconFormIndicatorWarning />,
          description,
          buttonText: t('confirmButton'),
          buttonIcon: <IconMove />,
          buttonVariant: 'primary-blue',
          onConfirm: () => moveMutation.mutate(moveData),
          children: (
            <Stack space={4}>
              {fieldMappingNodes.length > 0 &&
                renderBreakingNodes(fieldMappingNodes, t('confirmActionLabel'))}
              {relationshipNodes.length > 0 &&
                renderBreakingNodes(relationshipNodes, t('confirmRelationshipLabel'))}
            </Stack>
          ),
        });
      } else {
        moveMutation.mutate(moveData);
      }
    }
  };

  const isMoveDisabled = Boolean(selectedNode);

  const moveContextValue = useMemo(() => {
    return {
      availableMoves,
      isMoveDisabled,
      isPreviewMode,
    };
  }, [availableMoves, isMoveDisabled, isPreviewMode]);

  if (journeyActionsQuery.isError) {
    return (
      <ErrorScreen code={500} heading={t('actionsError')} message={t('actionsErrorMessage')} />
    );
  }

  return (
    <>
      <JourneyContext.Provider
        value={{
          journeyActionForms,
          journeyForms: triggerForms,
          webhooks: triggerWebhooks,
          pollingConfigs: triggerPollingConfigs,
          triggersCount,
          journeyId: journey.shortId,
        }}
      >
        <TestIdContext.Provider value={shortIdToDescriptiveMap}>
          <MoveContext.Provider value={moveContextValue}>
            <CollapseContext.Provider value={collapseNodesFunctions}>
              <DndContext
                onDragStart={() => {
                  if (isPreviewMode) return;
                  hideSidebar();
                  setIsDragging(true);
                }}
                onDragEnd={(event) => {
                  if (isPreviewMode) return;
                  handleDragDrop(event);
                }}
              >
                <StyledBuilder
                  journey={journey}
                  isDragging={isDragging}
                  isPreviewMode={isPreviewMode}
                  viewportRef={viewportRef}
                />
              </DndContext>
            </CollapseContext.Provider>
          </MoveContext.Provider>
        </TestIdContext.Provider>
      </JourneyContext.Provider>
    </>
  );
};

const StyledBuilder = styled(JourneyBuilder)({
  // Needed to display dragging placeholder over nodes
  '.react-flow__node:focus-within': {
    zIndex: '1 !important',
  },
});

const locale = {
  en: {
    fields: 'Fields',
    journey: 'Journey',
    publish: 'Publish',
    publishChanges: 'Publish changes',
    cancelChanges: 'Cancel changes',
    versionHistory: 'Version history',
    published: 'Published',
    draft: 'Draft',
    issues: 'Issues \u2022 {num_issues}',
    noIssues: 'No issues',
    journeyNotConfigured:
      'Journey not configured, are there any empty actions or unconfigured actions?',
    journeyNotFound: 'Journey not found',
    deleteJourney: 'Delete',
    editJourneyDetails: 'Edit journey details',
    journeyTitle: 'Journey title',
    confirmTitle: 'Warning',
    confirmDescription:
      'Moving this action will break connected values between the actions below.\n\nAre you sure you want to move this action?',
    confirmDescriptionRelationship:
      'Moving this action will remove it from the Wait for other actions dependency.\n\nAre you sure you want to move this action?',
    confirmDescriptionBoth:
      'Moving this action will break connected values and remove it from Wait for other actions dependencies.\n\nAre you sure you want to move this action?',
    confirmButton: 'Move action',
    confirmActionLabel: 'Connected actions',
    confirmRelationshipLabel: 'Wait for other actions dependency',
    settings: 'Settings',
    actionsError: 'Unexpected error',
    actionsErrorMessage: 'Failed to load journey builder',
    publishing: 'Publishing...',
    moving: 'Moving...',
    publishChangesDescription:
      'This Journey has unpublished changes, would you like to publish them?',
    publishJourney: 'Publish Journey',
    skipForNow: 'Skip for now',
    dontShowAgain: "Don't show again for this Journey",
    keepAsIs: 'Keep as is',
  },
};
